"use client";

import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAddressByCep } from "@/store/modules/address/thunks";
import { AppDispatch, RootState } from "@/store";
import { clearAddress } from "@/store/modules/address/slice";

const useCepAutocomplete = (
  cep: string,
  onSuccess: (address: any) => void,
  onClear?: () => void,
  debounceMs: number = 300,
) => {
  const dispatch = useDispatch<AppDispatch>();
  const address = useSelector((state: RootState) => state.address);
  const lastFetchedCep = useRef<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Keep stable references to callbacks to avoid effect loops
  const onSuccessRef = useRef(onSuccess);
  const onClearRef = useRef(onClear);
  onSuccessRef.current = onSuccess;
  onClearRef.current = onClear;

  // Track last successful payload to prevent duplicate onSuccess calls
  const lastPayloadRef = useRef<string | null>(null);

  useEffect(() => {
    const cleanCep = (cep || "").replace(/\D/g, "");

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      if (cleanCep.length === 8) {
        if (lastFetchedCep.current !== cleanCep) {
          lastFetchedCep.current = cleanCep;
          dispatch(fetchAddressByCep(cleanCep));
        }
      } else {
        lastFetchedCep.current = null;
        dispatch(clearAddress());
        if (onClearRef.current) onClearRef.current();
        // Reset last payload so next valid fetch triggers onSuccess again
        lastPayloadRef.current = null;
      }
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [cep, debounceMs, dispatch]);

  useEffect(() => {
    // Only propagate when we have a loaded address (no loading, no error)
    if (!address.loading && !address.error) {
      const payload = {
        street: address.street || "",
        neighborhood: address.neighborhood || "",
        city: address.city || "",
        state: address.state || "",
        country: address.country || "",
      };

      // Only call onSuccess if we have some populated fields
      if (
        payload.street ||
        payload.neighborhood ||
        payload.city ||
        payload.state
      ) {
        const serialized = JSON.stringify(payload);
        if (serialized !== lastPayloadRef.current) {
          lastPayloadRef.current = serialized;
          onSuccessRef.current(payload);
        }
      }
    }
  }, [
    address.loading,
    address.error,
    address.street,
    address.neighborhood,
    address.city,
    address.state,
    address.country,
  ]);
};

export default useCepAutocomplete;
